import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PayPalButton({ getPayload, quantity }) {
  const containerRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/paypal/config");
        if (!data.enabled || cancelled) return;
        setEnabled(true);
        if (!window.paypal) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = `https://www.paypal.com/sdk/js?client-id=${data.client_id}&currency=USD&intent=capture`;
            s.onload = resolve;
            s.onerror = reject;
            document.body.appendChild(s);
          });
        }
        if (cancelled) return;
        setReady(true);
      } catch {
        // PayPal not configured — stay hidden
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !window.paypal || !containerRef.current) return;
    containerRef.current.innerHTML = "";
    window.paypal.Buttons({
      style: { layout: "vertical", shape: "pill", color: "gold", label: "paypal" },
      createOrder: async () => {
        const payload = getPayload();
        if (!payload) throw new Error("missing checkout fields");
        const { data } = await api.post("/paypal/orders", { ...payload, quantity });
        return data.order_id;
      },
      onApprove: async (d) => {
        try {
          const res = await api.post(`/paypal/orders/${d.orderID}/capture`);
          if (res.data.payment_status === "paid") {
            window.location.href = `${window.location.origin}/payment/success?session_id=${d.orderID}`;
          } else {
            toast.error("PayPal payment was not completed.");
          }
        } catch {
          toast.error("Could not confirm the PayPal payment.");
        }
      },
      onError: () => toast.error("PayPal checkout failed — complete every field above and try again."),
    }).render(containerRef.current);
  }, [ready, quantity, getPayload]);

  if (!enabled) return null;
  return (
    <div className="mt-2" data-testid="paypal-section">
      <div className="flex items-center gap-3 my-3">
        <span className="flex-1 h-px bg-white/10" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">or</span>
        <span className="flex-1 h-px bg-white/10" />
      </div>
      <div ref={containerRef} data-testid="paypal-button-container" />
    </div>
  );
}
